import React, { useMemo } from "react";
/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
/* eslint react-hooks/set-state-in-effect : ["warn"] */
import { useMediaById } from "../hooks/useMediaById";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "@coworker/locales";
import { MediaPlacement } from "../Molecules/MediaPlacement";
import { GreyStrip } from "../Atoms/GreyStrip";
import { ArticleTasksSection } from "../Organisms/ArticleTasksSection";
import { useTasksByArticle } from "../hooks/useTasksByArticle";
import { useMediasAndArticleCount } from "../hooks/useMediasAndArticleCount";
import {
  getPrimeTasks,
  getSortedMediasArticleCount,
  reduceMediasArticleCount,
} from "./ArticleView.helper";
import { ArticleViewSkeleton } from "../Skeletons/ArticleViewSkeleton";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { useArticlesByProductArticleId } from "../hooks/useArticlesByProductArticleId";
import { AggregatedArticle } from "../hooks/useAggregatedArticles";
import { ArticleListItemMobile } from "../styles/styles";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import LocalCombinationSubArticlePanel from "../Molecules/LocalCombinationSubArticlePanel";
import {
  isArticleInCombination,
  isCombinationArticleValid,
} from "../Helpers/articleHelpers";
import { useCombinationArticleData } from "../hooks/useCombinationArticle";
import { AggregatedItemListViewItem } from "@coworker/apprestructured/src/media-maintenance/components/aggregated-items/AggegatedItemListViewItem/AggregatedItemListViewItem";
import { MediaItemType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemType";
import { useEDSData } from "@coworker/app/src/components/RoomSettings/hooks/useEDSData";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { ItemPlacementInGroups } from "@coworker/apprestructured/src/media-maintenance/components/aggregated-items/ItemPlacementInGroups/ItemPlacementInGroups";
import { MainBackupItemSelector } from "@coworker/apprestructured/src/media-maintenance/components/aggregated-items/MainBackupItemSelector/MainBackupItemSelector";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { useTasksByMedia } from "@coworker/app/src/components/RoomSettings/hooks/useTasksByMedia";
import { AboutItemSection } from "@coworker/app/src/components/RoomSettings/Articles/AboutItemSection";
import { useSalesStopData } from "@coworker/app/src/components/RoomSettings/hooks/useSalesStopData";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";

export function AggregatedArticleView() {
  const { mediaId, articleId, productArticleId } = useParams();
  const [searchParams] = useSearchParams();

  const mediaItemType =
    (searchParams.get("media-item-type") as MediaItemType) || undefined;

  const { t } = useTranslation();
  const navigate = useFixaNavigate();
  const { data: articles, isLoading: articlesLoading } =
    useArticlesByProductArticleId(mediaId, productArticleId);
  const { data: mediaResponse, isLoading: mediaLoading } =
    useMediaById(mediaId);

  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, productArticleId || "");
  const { data: itemInfo, isLoading } = useItemInfo(productArticleId ?? "");
  const storeId = useStoreId();
  const isMediaSubItem = mediaItemType === MediaItemType.LC_SUBITEM;
  // If media is a subitem, we need to fetch the combination article to show the parent article info
  const { data: combinationArticle } = useCombinationArticleData(
    articleId,
    isMediaSubItem
  );
  const { taskLinks } = useTasksByMedia(mediaId);

  const article =
    articles && articles.length > 0
      ? articles.filter((article) => article.id === articleId)[0]
      : undefined;
  const { data: salesStopData } = useSalesStopData(
    storeId,
    article?.productArticleId ?? ""
  );

  const { tasks } = useTasksByArticle(
    mediaResponse?.media.storeId,
    productArticleId
  );
  const { data: mediasArticleCount, isLoading: racLoading } =
    useMediasAndArticleCount(productArticleId, mediaResponse?.media.storeId);

  const sortedMediasArticleCount = getSortedMediasArticleCount(
    mediasArticleCount || [],
    mediaId
  );
  const primeTasks = getPrimeTasks(mediasArticleCount || [], tasks);

  const linkbar = useMemo(() => {
    if (!mediaResponse?.media.name || !mediaResponse?.media.id) {
      return <LinkbarLoading />;
    }

    const linkBarItems = [
      {
        label: t("mediaMaintenanceString"),
        to: "/mediamaintenance",
      },
      {
        label: mediaResponse.media.name,
        to: `/mediamaintenance/tabs/${mediaResponse.media.id}?tab=article-list-tab`,
      },
    ];

    if (mediaResponse.parent?.name) {
      linkBarItems.splice(1, 0, {
        label: mediaResponse.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse.parent.id}?tab=article-list-tab`,
      });
    }

    return (
      <Linkbar
        items={linkBarItems}
        currentLabel={article?.itemName ?? ""}
        currentAsH1
      />
    );
  }, [
    article?.itemName,
    mediaResponse?.media.id,
    mediaResponse?.media.name,
    t,
    mediaResponse?.parent?.name,
    mediaResponse?.parent?.id,
  ]);

  if (articlesLoading || mediaLoading || racLoading)
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <ArticleViewSkeleton />
      </FullScreenPopup>
    );

  if (!article) navigate(`/mediamaintenance/tabs/${mediaId}`);

  return (
    <>
      {article && mediaResponse && (
        <FullScreenPopup noPadding linkbar={linkbar}>
          {(isArticleInCombination(article) || isMediaSubItem) && (
            <LocalCombinationSubArticlePanel
              parentArticle={
                isCombinationArticleValid(combinationArticle)
                  ? combinationArticle
                  : undefined
              }
            />
          )}
          <ArticleListItemMobile>
            <AggregatedItemListViewItem
              article={
                {
                  ...article,
                  isFromArticleView: true,
                  aggregatedArticles: articles,
                } as AggregatedArticle
              }
              media={mediaResponse.media}
              mediaItemType={mediaItemType ?? MediaItemType.REGULAR_ITEM}
              taskLinks={taskLinks}
              edsData={edsData?.[0] || null}
              itemCardRenderType={ItemCardRenderType.static}
              salesStopData={salesStopData?.[0] || null}
              itemInfo={itemInfo ?? null}
              itemInfoLoading={isLoading}
            />
          </ArticleListItemMobile>
          <FixaListView id="article-view-list" size={"small"}>
            {!articlesLoading && articles && (
              <>
                <MainBackupItemSelector
                  mediaId={mediaId || ""}
                  article={article}
                  mediaResponse={mediaResponse}
                  allArticles={articles ?? []}
                  combinationArticle={combinationArticle}
                />
                <ItemPlacementInGroups articles={articles ?? []} />
              </>
            )}

            <GreyStrip text={t("inMediaString")} />

            <MediaPlacement
              mediasArticleCount={
                reduceMediasArticleCount(
                  sortedMediasArticleCount.map((mediaArticle) => {
                    return { ...mediaArticle };
                  })
                ) || []
              }
              currentArticleId={article.id}
            />

            <GreyStrip text={t("aboutArticleString")} />
            <AboutItemSection article={article} />
          </FixaListView>
          {(!isArticleInCombination(article) || !isMediaSubItem) && (
            <ArticleTasksSection
              mediasArticleCount={sortedMediasArticleCount || []}
              tasks={primeTasks}
              currentArticleId={article.id}
            />
          )}
        </FullScreenPopup>
      )}
    </>
  );
}
